import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface AgentStatus {
  _id: string;
  name: string;
  displayName: string;
  description: string;
  color: string;
  icon: string;
  canReceiveCalls: boolean;
  isAvailable: boolean;
  isBreak: boolean;
  isOffline: boolean;
  maxDuration?: number;
  autoLogoutAfter?: number;
  requiresReason: boolean;
  allowedTransitions: string[];
  createdAt: string;
  updatedAt: string;
}

interface AgentStatusContextType {
  statuses: AgentStatus[];
  loading: boolean;
  error: string | null;
  fetchStatuses: () => Promise<void>;
  createStatus: (statusData: Partial<AgentStatus>) => Promise<AgentStatus>;
  updateStatus: (id: string, statusData: Partial<AgentStatus>) => Promise<AgentStatus>;
  deleteStatus: (id: string) => Promise<void>;
  getStatusById: (id: string) => AgentStatus | undefined;
  getStatusByName: (name: string) => AgentStatus | undefined;
  // Status tracking functions
  setAgentStatus: (agentId: string, statusId: string, reason?: string, extension?: string) => Promise<boolean>;
  getAgentStatusDuration: (agentId: string) => string;
  getAgentCurrentStatus: (agentId: string) => AgentStatus | null;
  isAgentOnCall: (agentId: string) => boolean;
  updateAgentCallStatus: (agentId: string, onCall: boolean) => void;
}

const AgentStatusContext = createContext<AgentStatusContextType | undefined>(undefined);

export const useAgentStatus = () => {
  const context = useContext(AgentStatusContext);
  if (!context) {
    throw new Error('useAgentStatus must be used within an AgentStatusProvider');
  }
  return context;
};

interface AgentStatusProviderProps {
  children: ReactNode;
}

export const AgentStatusProvider: React.FC<AgentStatusProviderProps> = ({ children }) => {
  const [statuses, setStatuses] = useState<AgentStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentStatusTimers, setAgentStatusTimers] = useState<Map<string, { startTime: number; statusId: string }>>(new Map());
  const [agentCallStatus, setAgentCallStatus] = useState<Map<string, boolean>>(new Map());

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/agent-statuses');
      console.log('🔍 AgentStatusContext: API response:', response.data);
      if (response.data.success) {
        setStatuses(response.data.data);
        console.log('✅ AgentStatusContext: Statuses set:', response.data.data);
      } else {
        console.error('❌ AgentStatusContext: API error:', response.data.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Statüler yüklenirken hata oluştu');
      console.error('Agent status fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const createStatus = async (statusData: Partial<AgentStatus>): Promise<AgentStatus> => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/agent-statuses', statusData);
      const newStatus = response.data;
      setStatuses(prev => [...prev, newStatus]);
      return newStatus;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Statü oluşturulurken hata oluştu');
      console.error('Agent status create error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, statusData: Partial<AgentStatus>): Promise<AgentStatus> => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.put(`/agent-statuses/${id}`, statusData);
      const updatedStatus = response.data;
      setStatuses(prev => prev.map(status => 
        status._id === id ? updatedStatus : status
      ));
      return updatedStatus;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Statü güncellenirken hata oluştu');
      console.error('Agent status update error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteStatus = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/agent-statuses/${id}`);
      setStatuses(prev => prev.filter(status => status._id !== id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Statü silinirken hata oluştu');
      console.error('Agent status delete error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getStatusById = (id: string): AgentStatus | undefined => {
    return statuses.find(status => status._id === id);
  };

  const getStatusByName = (name: string): AgentStatus | undefined => {
    return statuses.find(status => status.name === name);
  };

  // Status tracking functions
  const setAgentStatus = async (agentId: string, statusId: string, reason?: string, extension?: string): Promise<boolean> => {
    try {
      setError(null);
      
      let targetAgentId = agentId;
      if (!targetAgentId || targetAgentId === 'undefined') {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const userObj = JSON.parse(userStr);
            targetAgentId = userObj._id || userObj.id;
          } catch (e) {}
        }
      }

      if (!targetAgentId || !statusId) {
        console.warn('⚠️ setAgentStatus skipped: missing targetAgentId or statusId', { targetAgentId, statusId });
        return false;
      }

      // Get extension from localStorage if not provided
      const sipExtension = extension || localStorage.getItem('agentExtension') || '';
      
      const response = await api.post('/agent-statuses/set-agent-status', {
        agentId: targetAgentId,
        statusId,
        extension: sipExtension,
        reason
      });
      
      if (response.data.success) {
        // Update local timer
        setAgentStatusTimers(prev => {
          const newMap = new Map(prev);
          newMap.set(agentId, { startTime: Date.now(), statusId });
          return newMap;
        });
        
        console.log('✅ Agent status updated:', response.data);
        console.log('📞 Asterisk queue updated:', response.data.data?.canReceiveCalls ? 'Ready for calls' : 'Paused');
        
        return true;
      }
      return false;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Statü değiştirilemedi');
      console.error('Set agent status error:', err);
      return false;
    }
  };

  const getAgentStatusDuration = (agentId: string): string => {
    const timer = agentStatusTimers.get(agentId);
    if (!timer) return '0:00';
    
    const elapsed = Math.floor((Date.now() - timer.startTime) / 1000);
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getAgentCurrentStatus = (agentId: string): AgentStatus | null => {
    const timer = agentStatusTimers.get(agentId);
    if (!timer) return null;
    
    return getStatusById(timer.statusId) || null;
  };

  const isAgentOnCall = (agentId: string): boolean => {
    return agentCallStatus.get(agentId) || false;
  };

  const updateAgentCallStatus = (agentId: string, onCall: boolean) => {
    setAgentCallStatus(prev => {
      const newMap = new Map(prev);
      newMap.set(agentId, onCall);
      return newMap;
    });
  };

  // Update status duration every second
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render to update duration display
      setAgentStatusTimers(prev => new Map(prev));
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const value: AgentStatusContextType = {
    statuses,
    loading,
    error,
    fetchStatuses,
    createStatus,
    updateStatus,
    deleteStatus,
    getStatusById,
    getStatusByName,
    setAgentStatus,
    getAgentStatusDuration,
    getAgentCurrentStatus,
    isAgentOnCall,
    updateAgentCallStatus,
  };

  return (
    <AgentStatusContext.Provider value={value}>
      {children}
    </AgentStatusContext.Provider>
  );
};

export default AgentStatusContext;
